import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import apiClient from "../shared/config/axiosConfig.jsx";
import {
  sanitizeFileName,
  formatDateForFileName,
  checkPageSpace,
} from "./pdf/utils.js";
import { addHeader, addFooter, addWatermark } from "./pdf/headerFooter.js";
import {
  renderInformacionGeneral,
  renderImportanteSection,
  renderTrabajoARealizarSection,
  renderActividadesRealizadasSection,
  renderObservacionesSection,
  renderComentariosSection,
  renderGenericSection,
} from "./pdf/sectionRenderers.js";
import { renderChecklistSection } from "./pdf/checklistRenderer.js";
import {
  renderImagenesEvidenciaSection,
  renderFirmasSection,
  renderMatrizCausaEfectoSection,
} from "./pdf/imageRenderers.js";

/**
 * Aplanar (flatten) el PDF para evitar que se puedan modificar las imágenes y firmas
 * Convierte todos los elementos interactivos en elementos estáticos
 * @param {ArrayBuffer} pdfBytes - Buffer del PDF generado por jsPDF
 * @returns {Promise<Uint8Array>} - PDF aplanado
 */
const flattenPDF = async (pdfBytes) => {
  try {
    const pdfDoc = await PDFDocument.load(pdfBytes);

    // Obtener todas las páginas
    const pages = pdfDoc.getPages();

    // Aplanar cada página (convertir todos los objetos en elementos fijos)
    for (const page of pages) {
      // pdf-lib automáticamente aplana el contenido al guardar
      // pero podemos forzar la eliminación de anotaciones interactivas
      const annotations = page.node.Annots();
      if (annotations) {
        page.node.delete("Annots");
      }
    }

    // Establecer metadatos de seguridad
    pdfDoc.setTitle("Reporte de Mantenimiento - DARPHA");
    pdfDoc.setProducer("DARPHA Fire Solutions");
    pdfDoc.setCreator("Sistema de Reportes DARPHA");
    pdfDoc.setCreationDate(new Date());
    pdfDoc.setModificationDate(new Date());

    // Guardar el PDF aplanado
    const flattenedPdfBytes = await pdfDoc.save({
      useObjectStreams: false, // Mejor compatibilidad
      addDefaultPage: false,
    });

    return flattenedPdfBytes;
  } catch (error) {
    console.error("Error al aplanar el PDF:", error);
    throw new Error("No se pudo aplanar el PDF: " + error.message);
  }
};

/**
 * Sellar el PDF con información de la empresa (firma digital simulada)
 * NOTA: Para firma digital real con certificado, se requiere un servidor backend
 * Esta función agrega metadatos y marca el documento como generado por la empresa
 * @param {Uint8Array} pdfBytes - Buffer del PDF aplanado
 * @returns {Promise<Uint8Array>} - PDF sellado
 */
const sealPDF = async (pdfBytes) => {
  try {
    const pdfDoc = await PDFDocument.load(pdfBytes);

    // Agregar metadatos de sellado digital
    const sealInfo = {
      company: "DARPHA Fire Solutions",
      timestamp: new Date().toISOString(),
      version: "1.0",
      certificateInfo:
        "Documento generado y validado por DARPHA Fire Solutions",
    };

    // Establecer palabras clave con información del sello
    pdfDoc.setKeywords([
      "DARPHA",
      "Fire Solutions",
      "Mantenimiento",
      `Sellado: ${sealInfo.timestamp}`,
      "Documento Oficial",
    ]);

    // Agregar información del sello en el subject
    pdfDoc.setSubject(
      `${sealInfo.certificateInfo} - Sellado el ${new Date().toLocaleDateString(
        "es-ES"
      )} a las ${new Date().toLocaleTimeString("es-ES")}`
    );

    // Marcar como documento final (solo lectura recomendada)
    pdfDoc.setAuthor("DARPHA Fire Solutions - Sistema Automatizado");

    // Guardar el PDF sellado
    const sealedPdfBytes = await pdfDoc.save({
      useObjectStreams: false,
      addDefaultPage: false,
    });

    console.log("✅ PDF sellado digitalmente con información de la empresa");

    return sealedPdfBytes;
  } catch (error) {
    console.error("Error al sellar el PDF:", error);
    // Si falla el sellado, retornar el PDF sin sellar pero aplanado
    return pdfBytes;
  }
};

// Generador de PDF
export const generatePDF = async (report, { idTipoMantenimiento } = {}) => {
  return new Promise(async (resolve, reject) => {
    try {
      let orientacion = report.orientacionpdf;
      const formatoId = idTipoMantenimiento || report.idtipomantenimiento || report.idTipoMantenimiento;
      if (formatoId) {
        // Una URL nueva evita reutilizar la configuración guardada por la PWA.
        const { data: formato } = await apiClient.get(`/tiposmantenimiento/${encodeURIComponent(formatoId)}`, {
          params: { _pdfOrientation: Date.now() },
        });
        if (!["portrait", "landscape"].includes(formato.orientacionpdf)) {
          throw new Error("El formato no tiene una orientación de PDF válida.");
        }
        orientacion = formato.orientacionpdf;
      }
      // ✅ Cambiar de A4 a tamaño carta (8.5 x 11 pulgadas)
      const doc = new jsPDF({
        orientation: orientacion === "landscape" ? "landscape" : "portrait",
        unit: "mm",
        format: "letter", // Tamaño carta: 8.5 x 11 pulgadas (215.9 x 279.4 mm)
      });
      doc.tipoMantenimiento = report.tipoMantenimiento;

      // Configurar eventos para footers automáticos
      doc.internal.events.subscribe("addPage", function () {
        addFooter(doc);
        addWatermark(doc); // Agregar marca de agua en cada página nueva
      });

      let currentY = 10; // Posición vertical inicial

      // Agregar encabezado a la primera página
      addHeader(doc, currentY, report.tipoMantenimiento);
      currentY += 30; // Ajustar la posición vertical después del encabezado

      // Agregar footer y marca de agua a la primera página
      addFooter(doc);
      addWatermark(doc);

      // Renderizar cada sección
      for (const seccion of report.secciones) {
        switch (seccion.nombreseccion) {
          case "Información General":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderInformacionGeneral(doc, seccion, report, currentY);
            break;
          case "Importante":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderImportanteSection(doc, seccion, currentY);
            break;
          case "Trabajo a Realizar":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderTrabajoARealizarSection(doc, seccion, currentY);
            break;
          case "Actividades Realizadas":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderActividadesRealizadasSection(
              doc,
              seccion,
              currentY
            );
            break;
          case "Checklist":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = await renderChecklistSection(
              doc,
              seccion,
              report,
              currentY
            );
            break;
          case "Comentarios":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderComentariosSection(doc, seccion, currentY);
            break;
          case "Observaciones":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderObservacionesSection(doc, seccion, currentY);
            break;
          case "Conclusiones/Recomendaciones":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderComentariosSection(doc, seccion, currentY);
            break;
          case "Firmas de conformidad":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = await renderFirmasSection(
              doc,
              report,
              seccion,
              currentY
            );
            break;
          case "Imágenes de evidencia":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = await renderImagenesEvidenciaSection(
              doc,
              seccion,
              currentY
            );
            break;
          case "Matriz Causa y Efecto":
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = await renderMatrizCausaEfectoSection(
              doc,
              seccion,
              currentY
            );
            break;
          default:
            currentY = checkPageSpace(doc, currentY, 60, addHeader);
            currentY = renderGenericSection(doc, seccion, currentY);
            break;
        }
      }

      // ✅ Crear nombre de archivo personalizado
      const plantaLimpia = sanitizeFileName(report.planta);
      const tipoMantenimientoLimpio = sanitizeFileName(
        report.tipoMantenimiento
      );
      const fechaFormateada = formatDateForFileName(report.fecha);

      // Formato: Planta_TipoMantenimiento_Fecha.pdf
      const nombreArchivo = `${plantaLimpia}_${tipoMantenimientoLimpio}_${fechaFormateada}.pdf`;

      // ✅ Obtener el PDF como ArrayBuffer antes de descargarlo
      const pdfArrayBuffer = doc.output("arraybuffer");

      console.log("🔒 Aplicando seguridad al PDF...");

      // ✅ Aplanar el PDF para evitar modificaciones
      const flattenedPdf = await flattenPDF(pdfArrayBuffer);

      // ✅ Sellar el PDF con información de la empresa
      const sealedPdf = await sealPDF(flattenedPdf);

      // ✅ Crear un Blob con el PDF procesado
      const blob = new Blob([sealedPdf], { type: "application/pdf" });

      // ✅ Descargar el PDF seguro
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      console.log("✅ PDF generado, aplanado y sellado exitosamente");

      resolve(); // Resolver la promesa cuando el PDF esté listo
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      reject(error); // Rechazar la promesa en caso de error
    }
  });
};
